import { useState, useCallback, useMemo, useEffect } from 'react';
import { debounce } from 'lodash';
import useConversations from './useConversations';
import { applyConversationClientSideFilters } from '../Conversations/conversationFilterUtils';

/**
 * Unified hook to manage conversation pagination, filtering, search, and sorting
 * Consolidates logic from useConversationPagination, useConversationFiltering, and useInitialConversationLoad
 * 
 * @param {Object} params - Configuration parameters
 * @param {Array} params.conversations - Array of conversations
 * @param {string} params.selectedAgent - Currently selected agent ID
 * @param {Function} params.setConversations - Function to update conversations list
 * @param {Function} params.onSelectConversation - Callback to select a conversation
 * @returns {Object} All pagination, filtering, and sorting state and handlers
 */
const useConversationManager = ({
    conversations,
    selectedAgent,
    setConversations,
    onSelectConversation,
}) => {
    // ===== Pagination State =====
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [loading, setLoading] = useState(false);
    const perPage = 25;

    // ===== Filtering State =====
    const [searchText, setSearchText] = useState('');
    const [chipFilters, setChipFilters] = useState([]);
    const [normalizedChipFilters, setNormalizedChipFilters] = useState({});
    const [sortBy, setSortBy] = useState('updated_time');
    const [sortDirection, setSortDirection] = useState('desc');

    const { fetchConversations } = useConversations();

    // ===== Unified Fetch Function =====
    /**
     * Single function to fetch conversations with all parameters
     * Eliminates duplicate API calls from separate hooks
     */
    const fetchWithCurrentParams = useCallback((overrides = {}) => {
        if (!selectedAgent) return;

        const params = {
            page: currentPage,
            perPage,
            selectedAgent,
            setConversations,
            setHasNextPage,
            setLoading,
            search_string: searchText,
            filters: normalizedChipFilters || {},
            sort_by: sortBy,
            sort_direction: sortDirection,
            shouldAppend: false,
            ...overrides
        };

        return fetchConversations(params);
    }, [
        currentPage,
        perPage,
        selectedAgent,
        setConversations,
        searchText,
        normalizedChipFilters,
        sortBy,
        sortDirection,
        fetchConversations
    ]);

    // ===== Pagination Handlers =====
    const handleScroll = useCallback((e) => {
        if (!hasNextPage || loading) return;

        const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;

        // When user reaches the bottom (with tiny tolerance), load more data
        if (scrollTop + clientHeight >= scrollHeight - 2) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            fetchWithCurrentParams({
                page: nextPage,
                shouldAppend: true
            });
        }
    }, [hasNextPage, loading, currentPage, fetchWithCurrentParams]);

    const resetPagination = useCallback(() => {
        setCurrentPage(1);
        setHasNextPage(true);
    }, []);

    // ===== Filtering Handlers =====

    // Debounced search handler
    const debouncedSearch = useCallback(
        debounce((searchValue) => {
            resetPagination();
            fetchWithCurrentParams({
                page: 1,
                search_string: searchValue,
                shouldAppend: false
            });
        }, 500),
        [fetchWithCurrentParams, resetPagination]
    );

    const handleSearchChange = useCallback((e) => {
        const search = e.target.value;
        setSearchText(search);

        // Clear current list and show skeleton while searching
        setConversations([]);
        setHasNextPage(true);
        setLoading(true);

        debouncedSearch(search);
    }, [setConversations, debouncedSearch]);

    const handleFiltersChange = useCallback((nextFilters, normalized) => {
        setChipFilters(nextFilters);
        setNormalizedChipFilters(normalized);
        resetPagination();

        // Clear and refetch with new filters
        setConversations([]);
        setHasNextPage(true);

        fetchWithCurrentParams({
            page: 1,
            filters: normalized || {},
            shouldAppend: false
        });
    }, [resetPagination, setConversations, fetchWithCurrentParams]);

    const handleSortChange = useCallback(({ sort_by, sort_direction }) => {
        setSortBy(sort_by || 'updated_time');
        setSortDirection(sort_direction || 'desc');
        resetPagination();

        setConversations([]);
        setHasNextPage(true);

        fetchWithCurrentParams({
            page: 1,
            sort_by: sort_by || 'updated_time',
            sort_direction: sort_direction || 'desc',
            shouldAppend: false
        });
    }, [resetPagination, setConversations, fetchWithCurrentParams]);

    // ===== Initial Load Effect =====
    /**
     * Load conversations when agent changes
     * Clears selection, resets pagination, and fetches fresh data
     */
    useEffect(() => {
        if (selectedAgent) {
            // Clear currently selected conversation when agent changes
            if (onSelectConversation) {
                onSelectConversation(null);
            }
            resetPagination();
            setConversations([]);
            setLoading(true);
            fetchConversations({
                page: 1,
                perPage,
                selectedAgent,
                setConversations,
                setHasNextPage,
                setLoading,
                shouldAppend: false,
                filters: {},
                sort_by: sortBy,
                sort_direction: sortDirection,
            });
        }
        // Only depend on selectedAgent - sort/filter changes are handled by their respective handlers
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgent]);

    // ===== Client-Side Filtering =====
    /**
     * Apply client-side filters (search and chip filters)
     * Server returns sorted data, we just filter locally
     */
    const sortedConversations = useMemo(() => {
        if (!conversations || !Array.isArray(conversations)) return [];

        return applyConversationClientSideFilters({
            conversations,
            searchText,
            normalizedFilters: normalizedChipFilters,
        });
    }, [conversations, searchText, normalizedChipFilters]);

    // ===== Return All State and Handlers =====
    return {
        // Pagination
        hasNextPage,
        loading,
        handleScroll,

        // Filtering and Sorting
        chipFilters,
        sortBy,
        sortDirection,
        sortedConversations,
        handleSearchChange,
        handleFiltersChange,
        handleSortChange,
    };
};

export default useConversationManager;
